import React, { useEffect, useState } from "react";
import { BookOpenText, Building2, ChevronLeft, ChevronRight, Download, MapPin, Pencil, Plus, Search, Trash2, University } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { apiFetch, axiosReq } from "../../commons/axiosReq";
import { AddModalUniversity, DeleteModalWriters } from "../../components";
import UniversityAcademicStructureManager from "../../components/MainAdmin/UniversityAcademicStructureManager";
import UniversityImporter from "../../components/MainAdmin/UniversityImporter";
import UniversityProfileEditor from "../../components/MainAdmin/UniversityProfileEditor";
import "./MainAdminUniversities.css";
import "./UniversityProfileActions.css";

const MainAdminUniversities = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [countryId, setCountryId] = useState("");
  const [college, setCollege] = useState("");
  const [field, setField] = useState("");
  const [page, setPage] = useState(1);
  const [row, setRow] = useState(10);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (!new Cookies().get("token")) navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const loadCountries = async () => {
      const response = await axiosReq("University/GetCountries", "get");
      setCountries((Array.isArray(response) ? response : []).map((item) => ({ value: item.countryId, label: item.name })));
    };
    loadCountries();
  }, []);

  useEffect(() => {
    let active = true;
    const loadItems = async () => {
      setLoading(true);
      setError("");
      const params = new URLSearchParams({ page, pageSize: row, search: search.trim() });
      if (countryId) params.set("countryId", countryId);
      if (college.trim()) params.set("college", college.trim());
      if (field.trim()) params.set("field", field.trim());
      const response = await axiosReq(`University/GetUniversitiesForAdmin?${params}`, "get");
      if (!active) return;
      if (!response || response === "BadRequest") {
        setItems([]);
        setCount(0);
        setError("دریافت دانشگاه‌ها با خطا روبه‌رو شد.");
      } else {
        setItems(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
        setCount(Number(response?.count ?? (Array.isArray(response) ? response.length : 0)));
      }
      setLoading(false);
    };
    loadItems();
    return () => { active = false; };
  }, [page, reloadKey, row, search, countryId, college, field]);

  const toggleActive = async (item) => {
    setUpdatingId(item.universityId);
    setStatusError("");
    const result = await apiFetch(`University/SetUniversityActive/${item.universityId}`, "put", { isActive: !item.isActive });
    setUpdatingId(null);
    if (!result.ok) { setStatusError(result.error || "تغییر وضعیت انجام نشد."); return; }
    setItems((current) => current.map((entry) => entry.universityId === item.universityId ? { ...entry, isActive: result.data.isActive } : entry));
  };

  const refresh = () => setReloadKey((value) => value + 1);
  const start = count === 0 ? 0 : (page - 1) * row + 1;
  const end = Math.min(page * row, count);
  const visibleCollegeCount = items.reduce((sum, item) => sum + Number(item.collegeCount || 0), 0);
  const visibleCountryCount = new Set(items.map((item) => item.city?.country?.countryId).filter(Boolean)).size;

  return <div className="universities-page" dir="rtl">
    <header className="universities-header">
      <div className="universities-heading"><span><University size={24} /></span><div><h1>مدیریت دانشگاه‌ها</h1><p>دانشگاه‌ها، موقعیت جغرافیایی و ساختار آموزشی را یکجا مدیریت کنید.</p></div></div>
      <div className="universities-toolbar">
        <label className="universities-search"><Search size={18} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="جستجوی دانشگاه، شهر یا کشور..." /></label>
        <button className="universities-add" onClick={() => setShowAddModal(true)}><Plus size={19} />افزودن دانشگاه</button>
      </div>
    </header>

    <section className="universities-filters" aria-label="فیلتر دانشگاه‌ها">
      <label>کشور<select value={countryId} onChange={(event) => { setCountryId(event.target.value); setPage(1); }}><option value="">همه کشورها</option>{countries.map((country) => <option key={country.value} value={country.value}>{country.label}</option>)}</select></label>
      <label>دانشکده<input value={college} onChange={(event) => { setCollege(event.target.value); setPage(1); }} placeholder="جستجوی نام دانشکده..." /></label>
      <label>رشته<input value={field} onChange={(event) => { setField(event.target.value); setPage(1); }} placeholder="جستجوی نام رشته..." /></label>
      <button type="button" disabled={!countryId && !college && !field && !search} onClick={() => { setCountryId(""); setCollege(""); setField(""); setSearch(""); setPage(1); }}>پاک کردن فیلترها</button>
    </section>

    <section className="universities-summary">
      <div><span><University size={19} /></span><p>تعداد دانشگاه‌ها</p><strong>{count.toLocaleString("fa-IR")}</strong></div>
      <div><span><Building2 size={19} /></span><p>دانشکده‌های صفحه جاری</p><strong>{visibleCollegeCount.toLocaleString("fa-IR")}</strong></div>
      <div><span><MapPin size={19} /></span><p>کشورهای صفحه جاری</p><strong>{visibleCountryCount.toLocaleString("fa-IR")}</strong></div>
    </section>

    <section className={`universities-structure universities-import ${showImporter ? "is-open" : ""}`}>
      <button className="universities-structure-toggle" onClick={() => setShowImporter((value) => !value)}><span><Download size={18} /><span><strong>ورود دانشگاه بر اساس رتبه جهانی</strong><small>کشور و بازه رتبه پژوهشی را انتخاب و نتایج OpenAlex را بررسی کنید.</small></span></span><b>{showImporter ? "بستن" : "باز کردن"}</b></button>
      {showImporter && <UniversityImporter countries={countries} onChanged={refresh} />}
    </section>

    <section className={`universities-structure ${showStructure ? "is-open" : ""}`}>
      <button className="universities-structure-toggle" onClick={() => setShowStructure((value) => !value)}><span><Building2 size={18} /><span><strong>افزودن سریع دانشکده و رشته</strong><small>ساختار آموزشی یک دانشگاه را بدون خروج از این صفحه تکمیل کنید.</small></span></span><b>{showStructure ? "بستن" : "باز کردن"}</b></button>
      {showStructure && <UniversityAcademicStructureManager universities={items} />}
    </section>

    {statusError && <div className="universities-state is-error" role="alert">{statusError}</div>}
    {loading ? <div className="universities-state">در حال دریافت دانشگاه‌ها...</div> : error ? <div className="universities-state is-error">{error}<button onClick={refresh}>تلاش دوباره</button></div> : items.length === 0 ? <div className="universities-state"><University size={38} /><strong>دانشگاهی پیدا نشد</strong><span>{countryId || college || field || search ? "دانشگاهی با این فیلترها پیدا نشد؛ فیلترها را تغییر دهید یا پاک کنید." : "می‌توانید دانشگاه جدیدی به سامانه اضافه کنید."}</span></div> :
      <section className="universities-grid">{items.map((item) => <article className="university-card" key={item.universityId}>
        <div className="university-card-head"><span><University size={24} /></span><div><small>دانشگاه</small><h2>{item.name || "بدون نام"}</h2></div><b><Building2 size={15} />{Number(item.collegeCount || 0).toLocaleString("fa-IR")} دانشکده</b></div>
        <div className="university-location"><MapPin size={18} /><div><small>موقعیت</small><strong>{item.city?.name || "شهر ثبت نشده"}</strong><span>{item.city?.country?.name || "کشور ثبت نشده"}</span></div></div>
        <div className="university-visibility"><span className={item.isActive ? "is-active" : "is-inactive"}>{item.isActive ? "فعال؛ قابل نمایش و انتخاب" : "غیرفعال؛ فقط در پنل مدیریت"}</span><button type="button" disabled={updatingId !== null} onClick={() => toggleActive(item)}>{updatingId === item.universityId ? "در حال ذخیره..." : item.isActive ? "غیرفعال کردن" : "فعال کردن"}</button></div>
        <div className="university-card-actions">
          <button className="primary" onClick={() => navigate("/mainAdmin/colleges", { state: { id: item.universityId } })}><Building2 size={17} />مدیریت دانشکده‌ها</button>
          <button className={`profile ${item.hasProfile ? "is-ready" : ""}`} onClick={() => { setSelectedItem(item); setShowProfileEditor(true); }}><BookOpenText size={17} />{item.hasProfile ? "ویرایش متن و عکس" : "افزودن متن و عکس"}</button>
          <button aria-label="ویرایش" onClick={() => { setSelectedItem(item); setShowEditModal(true); }}><Pencil size={17} /></button>
          <button className="danger" aria-label="حذف" onClick={() => { setSelectedItem(item); setShowDeleteModal(true); }}><Trash2 size={17} /></button>
        </div>
      </article>)}</section>}

    <footer className="universities-pagination">
      <label>تعداد در صفحه<select value={row} onChange={(event) => { setRow(Number(event.target.value)); setPage(1); }}><option value={5}>۵</option><option value={10}>۱۰</option><option value={25}>۲۵</option></select></label>
      <span>{start.toLocaleString("fa-IR")} تا {end.toLocaleString("fa-IR")} از {count.toLocaleString("fa-IR")}</span>
      <div><button disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronRight size={18} /></button><b>{page.toLocaleString("fa-IR")}</b><button disabled={end >= count} onClick={() => setPage((value) => value + 1)}><ChevronLeft size={18} /></button></div>
    </footer>

    {showAddModal && <AddModalUniversity title="افزودن دانشگاه جدید" buttonName="ثبت" setShowModal={setShowAddModal} reCheck={reloadKey} setRecheck={refresh} setData={setSelectedItem} country={countries} />}
    {showEditModal && <AddModalUniversity title="ویرایش" buttonName="ویرایش" country={countries} setShowModal={setShowEditModal} data={selectedItem} reCheck={reloadKey} setRecheck={refresh} />}
    {showDeleteModal && <DeleteModalWriters title="حذف دانشگاه" buttonName="حذف" text="آیا از حذف دانشگاه انتخاب‌شده اطمینان دارید؟" setShowModal={setShowDeleteModal} api={`University/DeleteUniversity/${selectedItem?.universityId}`} reCheck={reloadKey} setRecheck={refresh} />}
    {showProfileEditor && selectedItem && <UniversityProfileEditor university={selectedItem} onClose={() => setShowProfileEditor(false)} onSaved={refresh} />}
  </div>;
};

export default MainAdminUniversities;
